import moment from 'moment'
import React, { useState } from 'react'
import { 
  BsThreeDotsVertical, 
  BsSearch, 
  BsFilter, 
  BsEye, 
  BsDownload,
  BsPrinter,
  BsShare
} from 'react-icons/bs'
import { 
  FiSearch, 
  FiFilter, 
  FiEye, 
  FiDownload, 
  FiPrinter, 
  FiShare2,
  FiFileText,
  FiX,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiUser,
  FiBookOpen,
  FiCalendar,
  FiMoreVertical,
  FiAward,
  FiRefreshCw
} from "react-icons/fi";
import { 
  MdOutlineFileDownload, 
  MdOutlineVisibility, 
  MdOutlinePrint,
  MdOutlineShare,
  MdOutlineClose,
  MdOutlineFilePresent,
  MdOutlineDescription,
  MdOutlineVerified,
  MdOutlineCancel
} from "react-icons/md";
import { HiDocumentText } from 'react-icons/hi'
import { FaTimes } from 'react-icons/fa'
import { baseURL } from '../../../BaseURL'
import SortableHeader from '../../../components/SortableHeader';

const CertificateTable = ({ 
  filter, 
  setFilter, 
  handlePreview, 
  TableName, 
  TableHeader, 
  TableData, 
  showMenu, 
  handleChangeStatus, 
  editAction, 
  viewAction, 
  handleFilter, 
  handleCreateMarketing, 
  total, 
  pageNo, 
  size, 
  isMenu, 
  clickedUserId,
  currentSortKey,
  currentSortOrder,
  onSort
}) => {
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(filter?.keyWord || '');

  const handleSearch = (value) => {
    setSearchTerm(value);
    setFilter((p) => ({
      ...p,
      keyWord: value
    }));
  }

  const handleViewPdf = (learner) => {
    const certificateURL = learner.certificateURL.split("certificates/")[1];
    const fullPdfUrl = `${baseURL}/v1/course/previewCertificate?fileName=${certificateURL}`;
    setSelectedPdf(fullPdfUrl);
    setIsPdfModalOpen(true);
  }

  const closePdfModal = () => {
    setIsPdfModalOpen(false);
    setSelectedPdf(null);
  }

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'issued': { 
        icon: FiCheckCircle, 
        color: 'text-emerald-700 bg-emerald-50 border-emerald-200', 
        label: 'Issued' 
      },
      'active': { 
        icon: FiCheckCircle, 
        color: 'text-emerald-700 bg-emerald-50 border-emerald-200', 
        label: 'Active' 
      },
      'revoke': { 
        icon: FiXCircle, 
        color: 'text-rose-700 bg-rose-50 border-rose-200', 
        label: 'Revoked' 
      },
      'revoked': { 
        icon: FiXCircle, 
        color: 'text-rose-700 bg-rose-50 border-rose-200', 
        label: 'Revoked' 
      },
      'pending': { 
        icon: FiClock, 
        color: 'text-amber-700 bg-amber-50 border-amber-200', 
        label: 'Pending' 
      }
    };
    const config = statusConfig[status?.toLowerCase()] || statusConfig['pending'];
    const Icon = config.icon;
    
    return (
      <span className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
        ${config.color}
      `}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    return moment(date).format('MMM D, YYYY');
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100/80 overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 md:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-lg">
              <FiAward className="w-4 h-4 text-[#715EFE]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">{TableName}</h2>
              <p className="text-xs text-gray-400 hidden sm:block">
                {TableData?.list?.length || 0} certificates
              </p>
            </div>
          </div>
          <div className="relative flex-1 sm:min-w-[200px]">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by course name..."
              value={searchTerm}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                {TableHeader?.map((heading, index) => {
                  const headerIcons = {
                    '#': null,
                    'Certificate & ID': FiFileText,
                    'Learner Name': FiUser,
                    'Course': FiBookOpen,
                    'Status': FiClock,
                    'Issue Date': FiCalendar
                  };
                  const Icon = headerIcons[heading.label] || headerIcons[heading.label?.trim()];
                  return (
                    <td key={index} className="px-3 md:px-4 py-3 whitespace-nowrap">
                      <SortableHeader
                        label={heading.label}
                        sortKey={heading.sortKey}
                        currentSortKey={currentSortKey}
                        currentSortOrder={currentSortOrder}
                        onSort={onSort}
                        icon={Icon}
                      />
                    </td>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {TableData && TableData?.list && Array.isArray(TableData.list) && TableData.list.length > 0 ? (
                TableData?.list?.map((learner, index) => (
                  <tr key={index} className="hover:bg-gray-50/70 transition-colors duration-150 group">
                    <td className="px-3 md:px-4 py-3">
                      <span className="text-sm font-medium text-gray-400">
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      {learner?.certificateURL ? (
                        <button
                          className="flex items-center gap-2 text-[#715EFE] hover:text-[#855DF6] transition-colors duration-200 group"
                          onClick={() => handleViewPdf(learner)}
                        >
                          <div className="p-1.5 bg-[#715EFE]/10 rounded-lg group-hover:bg-[#715EFE]/20 transition-colors duration-200">
                            <HiDocumentText className="text-lg" />
                          </div>
                          <span className="text-sm font-medium">View Certificate</span>
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400">No certificate</span>
                      )}
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center">
                          <span className="text-xs font-bold text-[#715EFE]">
                            {learner?.user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-gray-800 truncate max-w-[100px]">
                          {learner?.user?.fullName || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <span className="text-sm text-gray-600 truncate max-w-[150px] block">
                        {learner?.course?.title || 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <StatusBadge status={learner?.status} />
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-700 whitespace-nowrap">
                          {formatDate(learner?.createdAt)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={TableHeader?.length || 6} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-4 bg-gray-50 rounded-full">
                        <FiAward className="w-10 h-10 text-gray-300" />
                      </div>
                      <p className="text-sm font-medium text-gray-500">No certificates found</p>
                      <p className="text-xs text-gray-400">Try adjusting your search</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {TableData?.list?.length > 0 && (
          <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Showing {TableData.list.length} certificate{TableData.list.length !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-gray-400">
              Last updated: {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        )}
      </div>

      {/* PDF Modal */}
      {isPdfModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl h-[90vh] flex flex-col animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-gradient-to-r from-gray-50/50 to-white rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-xl">
                  <HiDocumentText className="w-5 h-5 text-[#715EFE]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Certificate Preview</h3>
                  <p className="text-xs text-gray-400 hidden sm:block">PDF document preview</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Action Buttons */}
                <button
                  onClick={() => window.open(selectedPdf, '_blank')}
                  className="p-2 text-gray-400 hover:text-[#715EFE] hover:bg-[#715EFE]/10 rounded-lg transition-all duration-200"
                  title="Open in new tab"
                >
                  <FiEye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = selectedPdf;
                    link.download = 'certificate.pdf';
                    link.click();
                  }}
                  className="p-2 text-gray-400 hover:text-[#715EFE] hover:bg-[#715EFE]/10 rounded-lg transition-all duration-200"
                  title="Download PDF"
                >
                  <FiDownload className="w-4 h-4" />
                </button>
                <button
                  onClick={closePdfModal}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                  title="Close"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* PDF Viewer */}
            <div className="flex-1 p-2 bg-gray-50/50 overflow-hidden rounded-b-2xl">
              <iframe
                src={selectedPdf}
                className="w-full h-full border-0 rounded-lg"
                title="Certificate PDF Viewer"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CertificateTable